import cn from 'classnames';
import { useState } from 'react';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { CommentAnchor } from '../comments-constants';
import { CommentThread } from './CommentThread';
import { AuditReport, ScanSourceType } from '../digital-audit-constants';
import {
  FireSource,
  FireSourceType,
  PARAM_CANON,
  PARAM_DIM_TYPES,
  TagFire
} from '../engine/tag-fire-constants';
import {
  buildFireDifferences,
  buildFireMatrix,
  buildParamCoverage,
  extractTagFires,
  FireMatrix
} from '../engine/tag-fire-utils';
import {
  DataLayerVariableInventory,
  DataLayerVariableSource,
  DataLayerVariableSourceType
} from '../engine/datalayer-inventory-constants';
import { scanOriginLabel } from '../digital-audit-utils';
import { ObservationCard } from './ObservationCard';
import { ScanSourceBadge } from './ScanSourceBadge';

interface PixelIntelViewProps {
  report: AuditReport | null;
}

// Every section below is derived from report.scanResult (tagFires,
// dataLayerVariables), which a full-scan merge recomputes from real crawl
// evidence in place - so every section gets the same badge.
interface ScanSourceProps {
  scanSource: ScanSourceType;
  pageCoverage: AuditReport['fullScanPageCoverage'];
}

const MATRIX_MARK: Record<FireSourceType, string> = {
  [FireSource.WIRE]: '✓',
  [FireSource.CODE]: '◦',
  [FireSource.CONTAINER]: '◇'
};

const SOURCE_LABEL: Record<FireSourceType, string> = {
  [FireSource.WIRE]: 'on the wire',
  [FireSource.CODE]: 'in code',
  [FireSource.CONTAINER]: 'configured via tag manager'
};

interface FireDetailProps {
  fire: TagFire;
}

const FireDetail = ({ fire }: FireDetailProps) => {
  const paramEntries = Object.entries(fire.params);
  const scanOrigin = scanOriginLabel(fire.seenIn);

  return (
    <div className='digital-audit-fire'>
      <div className='digital-audit-fire-head'>
        <span className={cn('digital-audit-fire-source', fire.source)}>
          {SOURCE_LABEL[fire.source]}
        </span>
        <span className='digital-audit-fire-event'>{fire.event}</span>
        {fire.canonicalEvent && (
          <span className='digital-audit-fire-canon'>→ {fire.canonicalEvent}</span>
        )}
        {fire.id && <span className='digital-audit-fire-id'>{fire.id}</span>}
      </div>
      <div className='digital-audit-fire-meta'>
        trigger: {fire.trigger}
        {fire.origin && ` · seen in: ${fire.origin}`}
        {scanOrigin && ` · ${scanOrigin}`}
      </div>
      {paramEntries.length > 0 && (
        <table className='digital-audit-fire-params'>
          <tbody>
            {paramEntries.map(([key, value]) => (
              <tr key={key}>
                <td className='digital-audit-fire-param-key'>{key}</td>
                <td className='digital-audit-fire-param-value'>{value || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className='digital-audit-fire-raw'>{fire.raw}</div>
    </div>
  );
};

interface PlatformFiresCardProps {
  platform: string;
  fires: TagFire[];
}

const PlatformFiresCard = ({ platform, fires }: PlatformFiresCardProps) => {
  const [open, setOpen] = useState(false);
  const wireCount = fires.filter(f => f.source === FireSource.WIRE).length;
  // Union across every fire - a rollup for the collapsed header. Individual
  // fires can still differ (see FireDetail's own per-fire label once expanded).
  const scanOrigin = scanOriginLabel(fires.flatMap(f => f.seenIn ?? []));

  return (
    <div className='digital-audit-obs opportunity'>
      <InteractiveDiv className='digital-audit-obs-head' onClick={() => setOpen(v => !v)}>
        <span className='digital-audit-obs-title'>{platform}</span>
        <span className='digital-audit-fire-count'>
          {fires.length} call(s){wireCount > 0 && ` · ${wireCount} on the wire`}
          {scanOrigin && ` · ${scanOrigin}`}
        </span>
        <span className='digital-audit-obs-chev'>{open ? '−' : '+'}</span>
      </InteractiveDiv>
      {open && fires.map(f => <FireDetail key={`${f.source}-${f.event}-${f.raw}`} fire={f} />)}
    </div>
  );
};

const SOURCE_SHORT_LABEL: Record<DataLayerVariableSourceType, string> = {
  [DataLayerVariableSource.LIVE_SNAPSHOT]: 'live dataLayer',
  [DataLayerVariableSource.DATALAYER_PUSH]: 'dataLayer.push() call',
  [DataLayerVariableSource.GTM_CONTAINER]: 'GTM container variable'
};

interface DataLayerInventorySectionProps extends ScanSourceProps {
  inventory: DataLayerVariableInventory;
}

const DataLayerInventorySection = ({
  inventory,
  scanSource,
  pageCoverage
}: DataLayerInventorySectionProps) => {
  const [open, setOpen] = useState(false);
  const consumedSet = new Set(inventory.consumed);

  return (
    <div className='digital-audit-section'>
      <InteractiveDiv className='digital-audit-section-head' onClick={() => setOpen(v => !v)}>
        <div className='digital-audit-section-title'>
          Tagging variable inventory
          <span className='digital-audit-obs-chev'>{open ? '−' : '+'}</span>
        </div>
        <ScanSourceBadge scanSource={scanSource} pageCoverage={pageCoverage} />
        <div className='digital-audit-section-count'>
          {inventory.available.length} variable(s) discovered — {inventory.unused.length} not used
          by any decoded tag
        </div>
      </InteractiveDiv>
      {open && (
        <>
          <div className='digital-audit-matrix-scroll'>
            <table className='digital-audit-table'>
              <thead>
                <tr>
                  <th>Variable</th>
                  <th>Source</th>
                  <th>Used by a tag</th>
                </tr>
              </thead>
              <tbody>
                {inventory.available.map(key => (
                  <tr key={key}>
                    <td className='digital-audit-table-mono'>{key}</td>
                    <td>
                      {inventory.sources[key].map(source => SOURCE_SHORT_LABEL[source]).join(', ')}
                    </td>
                    <td>{consumedSet.has(key) ? '✓' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {inventory.unused.length > 0 && (
            <div className='digital-audit-obs-list'>
              <ObservationCard
                severity='NOTE'
                title={`${inventory.unused.length} variable(s) available but not used by any decoded tag`}
                note={inventory.unused.join(', ')}
              />
            </div>
          )}
        </>
      )}
      <CommentThread anchor={CommentAnchor.DATA_LAYER} />
    </div>
  );
};

interface EventCoverageSectionProps extends ScanSourceProps {
  matrix: FireMatrix;
  fireCount: number;
}

const EventCoverageSection = ({
  matrix,
  fireCount,
  scanSource,
  pageCoverage
}: EventCoverageSectionProps) => {
  const [open, setOpen] = useState(false);

  return (
    <div className='digital-audit-section'>
      <InteractiveDiv className='digital-audit-section-head' onClick={() => setOpen(v => !v)}>
        <div className='digital-audit-section-title'>
          Event coverage by platform
          <span className='digital-audit-obs-chev'>{open ? '−' : '+'}</span>
        </div>
        <ScanSourceBadge scanSource={scanSource} pageCoverage={pageCoverage} />
        <div className='digital-audit-section-count'>
          {fireCount} tracking calls decoded — ✓ observed on the wire · ◦ declared in code · ◇
          configured via tag manager · — not seen
        </div>
      </InteractiveDiv>
      {open && (
        <div className='digital-audit-matrix-scroll'>
          <table className='digital-audit-table digital-audit-matrix'>
            <thead>
              <tr>
                <th>Platform</th>
                {matrix.events.map(event => (
                  <th key={event} className='digital-audit-matrix-event'>
                    {event}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.platforms.map(platform => (
                <tr key={platform}>
                  <td className='digital-audit-chk-label'>{platform}</td>
                  {matrix.events.map(event => {
                    const cell = matrix.cells[platform][event];
                    const cellOrigin = scanOriginLabel(matrix.cellOrigins[platform]?.[event]);
                    return (
                      <td
                        key={event}
                        className={cn('digital-audit-matrix-cell', cell)}
                        title={cellOrigin ?? undefined}
                      >
                        {cell ? MATRIX_MARK[cell] : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <CommentThread anchor={CommentAnchor.EVENT_COVERAGE} />
    </div>
  );
};

export const PixelIntelView = ({ report }: PixelIntelViewProps) => {
  if (!report) {
    return (
      <div className='digital-audit-page'>
        <div className='digital-audit-empty-state'>Run a scan to see pixel intelligence.</div>
      </div>
    );
  }

  // Persisted scans from before tagFires existed: derive nothing gracefully.
  const fires =
    report.scanResult.tagFires ??
    extractTagFires([], report.scanResult.runtimeNetwork?.requests ?? []);

  if (fires.length === 0) {
    return (
      <div className='digital-audit-page'>
        <div className='digital-audit-empty-state'>
          No individual tracking calls were visible in this scan&apos;s evidence. Tags loaded via
          tag managers fire at runtime — a runtime capture will populate this view.
        </div>
      </div>
    );
  }

  const matrix = buildFireMatrix(fires);
  const coverage = buildParamCoverage(fires, report.input.clientType);
  const differences = buildFireDifferences(fires, matrix, report.input.clientType);
  const byPlatform = fires.reduce<Record<string, TagFire[]>>((acc, f) => {
    (acc[f.platform] = acc[f.platform] ?? []).push(f);
    return acc;
  }, {});
  const paramDims = Object.keys(PARAM_CANON).filter(dim => {
    const types = PARAM_DIM_TYPES[dim];
    return !types || types.includes(report.input.clientType);
  });

  return (
    <div className='digital-audit-page'>
      <EventCoverageSection
        matrix={matrix}
        fireCount={fires.length}
        scanSource={report.scanSource}
        pageCoverage={report.fullScanPageCoverage}
      />

      {coverage.length > 0 && (
        <div className='digital-audit-section'>
          <div className='digital-audit-section-head'>
            <div className='digital-audit-section-title'>Parameter completeness</div>
            <ScanSourceBadge
              scanSource={report.scanSource}
              pageCoverage={report.fullScanPageCoverage}
            />
            <div className='digital-audit-section-count'>
              what each platform actually receives on key events
            </div>
          </div>
          {coverage.map(row => (
            <div key={row.event}>
              <span className='digital-audit-obs-group-label'>{row.event}</span>
              <div className='digital-audit-matrix-scroll'>
                <table className='digital-audit-table digital-audit-matrix'>
                  <thead>
                    <tr>
                      <th>Platform</th>
                      {paramDims.map(dim => (
                        <th key={dim} className='digital-audit-matrix-event'>
                          {dim}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(row.byPlatform).map(([platform, dims]) => (
                      <tr key={platform}>
                        <td className='digital-audit-chk-label'>{platform}</td>
                        {paramDims.map(dim => (
                          <td
                            key={dim}
                            className={cn(
                              'digital-audit-matrix-cell',
                              dims.includes(dim) ? 'wire' : undefined
                            )}
                          >
                            {dims.includes(dim) ? '✓' : '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          <CommentThread anchor={CommentAnchor.PARAMETER_COMPLETENESS} />
        </div>
      )}

      {differences.length > 0 && (
        <div className='digital-audit-section'>
          <div className='digital-audit-section-head'>
            <div className='digital-audit-section-title'>Where platforms diverge</div>
            <ScanSourceBadge
              scanSource={report.scanSource}
              pageCoverage={report.fullScanPageCoverage}
            />
            <div className='digital-audit-section-count'>
              {differences.length} findings — expand for detail
            </div>
          </div>
          <div className='digital-audit-obs-list'>
            {differences.map(d => (
              <ObservationCard key={d.title} severity={d.severity} title={d.title} note={d.note} />
            ))}
          </div>
          <CommentThread anchor={CommentAnchor.PLATFORM_DIVERGENCE} />
        </div>
      )}

      <div className='digital-audit-section'>
        <div className='digital-audit-section-head'>
          <div className='digital-audit-section-title'>Every decoded call</div>
          <ScanSourceBadge
            scanSource={report.scanSource}
            pageCoverage={report.fullScanPageCoverage}
          />
          <div className='digital-audit-section-count'>
            per platform — parameters, triggers, and raw calls
          </div>
        </div>
        <div className='digital-audit-obs-list'>
          {Object.entries(byPlatform)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([platform, platformFires]) => (
              <PlatformFiresCard key={platform} platform={platform} fires={platformFires} />
            ))}
        </div>
        <CommentThread anchor={CommentAnchor.DECODED_CALLS} />
      </div>

      {report.scanResult.dataLayerVariables &&
        report.scanResult.dataLayerVariables.available.length > 0 && (
          <DataLayerInventorySection
            inventory={report.scanResult.dataLayerVariables}
            scanSource={report.scanSource}
            pageCoverage={report.fullScanPageCoverage}
          />
        )}
    </div>
  );
};
